import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatCurrency, formatDate } from '@/lib/utils';
import { isQuoteExpired, isExpirationApproaching, ORDER_STATUSES } from '../types';
import type { Quote, QuotesFilters, QuoteStatus, OrderColumnKey } from '../types';

// =============================================================================
// Status Badge
// =============================================================================

const statusVariantMap: Record<QuoteStatus, 'secondary' | 'warning' | 'success' | 'outline' | 'destructive' | 'default'> = {
  preparation: 'secondary',
  sent: 'warning',
  'waiting-po': 'warning',
  approved: 'success',
  confirmed: 'success',
  inprogress: 'default',
  'invoice-ready': 'default',
  'partially-invoiced': 'warning',
  invoiced: 'outline',
  cancelled: 'destructive',
  rejected: 'destructive',
  expired: 'destructive',
};

// =============================================================================
// Column metadata (single source of truth for filter types + getValue)
// =============================================================================

interface OrderColumnMeta {
  key: OrderColumnKey;
  filterType?: 'text' | 'date-range' | 'select' | 'single-select' | 'number-range';
  getValue: (item: Quote) => string | number | null;
  getFilterValue?: (item: Quote) => string | number | null;
  noFilter?: boolean;
}

interface FilterOption {
  label: string;
  value: string;
}

const ORDER_COLUMN_META: OrderColumnMeta[] = [
  { key: 'quoteNumber', filterType: 'text', getValue: (o) => o.quoteNumber },
  { key: 'status', filterType: 'select', getValue: (o) => o.status },
  { key: 'createdAt', filterType: 'date-range', getValue: (o) => o.createdDate },
  { key: 'acceptedDate', filterType: 'date-range', getValue: (o) => o.acceptedDate },
  { key: 'accountName', filterType: 'text', getValue: (o) => o.accountName },
  { key: 'projectName', filterType: 'text', getValue: (o) => o.projectName },
  {
    key: 'salesManager',
    filterType: 'single-select',
    getValue: (o) => o.salesManagerName,
    getFilterValue: (o) => o.salesManagerId,
  },
  { key: 'contractualManager', filterType: 'text', getValue: (o) => o.contractualManagerName },
  { key: 'total', filterType: 'number-range', getValue: (o) => parseFloat(o.total) || 0 },
  { key: 'expirationDate', filterType: 'date-range', getValue: (o) => o.expirationDate },
  { key: 'poNumber', filterType: 'text', getValue: (o) => o.poNumber },
  { key: 'actions', noFilter: true, getValue: () => '' },
];

const META_BY_KEY = new Map(ORDER_COLUMN_META.map((m) => [m.key, m]));

/** Apply client-side column filters to a page of orders (AND-combined). */
export function applyOrderColumnFilters(
  items: Quote[],
  columnFilters: Record<string, string>,
): Quote[] {
  const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
  if (activeFilters.length === 0) return items;

  return items.filter((item) =>
    activeFilters.every(([colKey, filterValue]) => {
      const meta = META_BY_KEY.get(colKey as OrderColumnKey);
      if (!meta || meta.noFilter) return true;

      const rawValue = meta.getValue(item);
      const filterableValue = meta.getFilterValue
        ? meta.getFilterValue(item)
        : rawValue;

      if (meta.filterType === 'date-range') {
        const [from, to] = filterValue.split('>>');
        const dateStr = String(rawValue || '');
        if (from && dateStr < from) return false;
        if (to && dateStr > to + 'T23:59:59') return false;
        return true;
      }

      if (meta.filterType === 'select') {
        try {
          const selected = JSON.parse(filterValue) as string[];
          return selected.length === 0 || selected.includes(String(rawValue || ''));
        } catch { return true; }
      }

      if (meta.filterType === 'single-select') {
        return !filterValue || String(filterableValue || '') === filterValue;
      }

      if (meta.filterType === 'number-range') {
        const [minStr, maxStr] = filterValue.split('>>');
        const num = Number(rawValue);
        if (isNaN(num)) return false;
        if (minStr && num < Number(minStr)) return false;
        if (maxStr && num > Number(maxStr)) return false;
        return true;
      }

      // Text: case-insensitive includes
      return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
    }),
  );
}

// =============================================================================
// Column definition (internal, with rendering)
// =============================================================================

interface OrderColumnDef extends OrderColumnMeta {
  label: string;
  sortKey?: QuotesFilters['sortKey'];
  filterOptions?: { label: string; value: string }[];
  render?: (item: Quote) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

// =============================================================================
// SortableHeader
// =============================================================================

interface SortableHeaderProps {
  label: string;
  sortKey: QuotesFilters['sortKey'];
  currentSortKey: QuotesFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: QuotesFilters['sortKey']) => void;
}

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: SortableHeaderProps) {
  const isActive = currentSortKey === sortKey;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter row rendering
// =============================================================================

function renderColumnFilter(
  col: OrderColumnDef,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  salesManagerOptions: FilterOption[],
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'single-select') {
    const options = col.key === 'salesManager' ? salesManagerOptions : [];
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Select
          value={columnFilters[col.key] || '__all__'}
          onValueChange={(value) =>
            setColumnFilter(col.key, value === '__all__' ? '' : value)
          }
        >
          <SelectTrigger
            aria-label={`Filter ${col.label}`}
            className="h-7 w-full min-w-0 text-xs font-normal"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableHead>
    );
  }

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// OrdersTable
// =============================================================================

interface OrdersTableProps {
  orders: Quote[];
  sortKey: QuotesFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: QuotesFilters['sortKey']) => void;
  onEdit?: (order: Quote) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  columnVisibility: Record<OrderColumnKey, boolean>;
  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;
  salesManagerOptions: FilterOption[];
  isLoading?: boolean;
}

export function OrdersTable({
  orders,
  sortKey,
  sortDirection,
  onSort,
  onEdit,
  page,
  pageSize,
  total,
  onPageChange,
  columnVisibility,
  columnFilters,
  setColumnFilter,
  salesManagerOptions,
  isLoading,
}: OrdersTableProps) {
  const { t } = useTranslation('orders');
  const totalPages = Math.ceil(total / pageSize) || 1;

  // Build column definitions from metadata + rendering details
  const columns: OrderColumnDef[] = [
    {
      ...META_BY_KEY.get('quoteNumber')!,
      label: t('table.orderNumber'),
      sortKey: 'quoteNumber',
      className: 'font-medium',
    },
    {
      ...META_BY_KEY.get('status')!,
      label: t('table.status'),
      filterOptions: ORDER_STATUSES.map((s) => ({ label: t(`status.${s}`), value: s })),
      render: (o) => {
        const expired = isQuoteExpired(o);
        const expiringSoon = isExpirationApproaching(o);
        return (
          <div className="flex items-center gap-2">
            <Badge variant={statusVariantMap[o.status] || 'secondary'}>
              {t(`status.${o.status}`)}
            </Badge>
            {expired && <Badge variant="destructive">{t('table.expired')}</Badge>}
            {expiringSoon && !expired && (
              <Badge variant="warning">{t('table.expiringSoon')}</Badge>
            )}
          </div>
        );
      },
    },
    {
      ...META_BY_KEY.get('createdAt')!,
      label: t('table.created'),
      sortKey: 'createdAt',
      render: (o) => <>{o.createdDate ? formatDate(o.createdDate) : '—'}</>,
    },
    {
      ...META_BY_KEY.get('acceptedDate')!,
      label: t('table.accepted'),
      sortKey: 'acceptedDate',
      render: (o) => <>{o.acceptedDate ? formatDate(o.acceptedDate) : '—'}</>,
    },
    {
      ...META_BY_KEY.get('accountName')!,
      label: t('table.customer'),
      sortKey: 'accountName',
      render: (o) =>
        o.accountId ? (
          <Link
            to={`/accounts/${o.accountId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-atek-blue hover:text-atek-blue-dark hover:underline"
          >
            {o.accountName || '—'}
          </Link>
        ) : (
          <>{o.accountName || '—'}</>
        ),
      className: 'max-w-40 truncate',
    },
    {
      ...META_BY_KEY.get('projectName')!,
      label: t('table.project'),
      sortKey: 'projectName',
      className: 'max-w-40 truncate',
    },
    {
      ...META_BY_KEY.get('salesManager')!,
      label: t('table.salesManager'),
      className: 'text-muted-foreground',
    },
    {
      ...META_BY_KEY.get('contractualManager')!,
      label: t('table.contact'),
      className: 'text-muted-foreground',
    },
    {
      ...META_BY_KEY.get('total')!,
      label: t('table.total'),
      sortKey: 'total',
      render: (o) => <>{formatCurrency(parseFloat(o.total) || 0, o.currency || 'CAD')}</>,
      align: 'right',
      className: 'font-medium',
    },
    {
      ...META_BY_KEY.get('expirationDate')!,
      label: t('table.expiration'),
      render: (o) => {
        const expired = isQuoteExpired(o);
        return (
          <span className={expired ? 'text-destructive font-medium' : ''}>
            {o.expirationDate ? formatDate(o.expirationDate) : '—'}
          </span>
        );
      },
    },
    {
      ...META_BY_KEY.get('poNumber')!,
      label: t('table.poNumber'),
    },
    {
      ...META_BY_KEY.get('actions')!,
      label: '',
      render: (o) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(o)}
              className="text-atek-blue hover:underline text-sm cursor-pointer"
            >
              {t('table.editOrder')}
            </button>
          )}
          {o.hubspotLink && (
            <a
              href={o.hubspotLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-7 w-7 text-muted-foreground hover:text-primary"
              title={t('table.hubspot')}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      ),
    },
  ];

  const visibleColumns = columns.filter((col) => columnVisibility[col.key] !== false);

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {/* ROW 1: Filter inputs (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(
                    col,
                    columnFilters,
                    setColumnFilter,
                    salesManagerOptions,
                  ),
                )}
              </TableRow>

            {/* ROW 2: Column labels + sort controls */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right')}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* Loading state */}
            {isLoading &&
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {/* Empty state */}
            {!isLoading && orders.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="py-12 text-center text-muted-foreground"
                >
                  {t('table.noResults')}
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading &&
              orders.map((order) => {
                const expired = isQuoteExpired(order);
                const expiringSoon = isExpirationApproaching(order);

                return (
                  <TableRow
                    key={order.id}
                    className={cn(
                      'hover:bg-muted/50',
                      expired && 'bg-red-50',
                      expiringSoon && !expired && 'bg-yellow-50',
                    )}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(order)
                          : String(col.getValue(order) ?? '') || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('table.pageOf', { current: page + 1, total: totalPages })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
            >
              {t('table.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
            >
              {t('table.next')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
